'use client';

import { useState, useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SortOption {
  id: string;
  label: string;
  icon: string;
}

interface SortDropdownProps {
  onSortChange: (sortBy: string) => void;
  currentSort: string;
}

const SortDropdown = ({ onSortChange, currentSort }: SortDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const sortOptions: SortOption[] = [
    { id: 'featured', label: 'Featured', icon: 'StarIcon' },
    { id: 'price-low', label: 'Price: Low to High', icon: 'ArrowUpIcon' },
    { id: 'price-high', label: 'Price: High to Low', icon: 'ArrowDownIcon' },
    { id: 'duration-short', label: 'Duration: Shortest', icon: 'ClockIcon' },
    { id: 'duration-long', label: 'Duration: Longest', icon: 'ClockIcon' },
    { id: 'rating', label: 'Highest Rated', icon: 'StarIcon' }
  ];

  const currentOption = sortOptions.find(opt => opt.id === currentSort) || sortOptions[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSortSelect = (sortId: string) => {
    onSortChange(sortId);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2.5 bg-card border border-border rounded-lg text-foreground hover:bg-surface transition-colors duration-200"
      >
        <Icon name={currentOption.icon as any} size={18} className="text-muted-foreground" />
        <span className="text-sm font-medium">{currentOption.label}</span>
        <Icon
          name={isOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'}
          size={18}
          className="text-muted-foreground"
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-popover rounded-lg shadow-brand-lg border border-border z-50 animate-fade-in">
          <div className="py-2">
            {sortOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => handleSortSelect(option.id)}
                className={`w-full flex items-center space-x-3 px-4 py-2.5 text-sm transition-colors duration-200 ${
                  currentSort === option.id
                    ? 'bg-surface text-primary font-medium' :'text-foreground hover:bg-surface'
                }`}
              >
                <Icon
                  name={option.icon as any}
                  size={18}
                  className={currentSort === option.id ? 'text-primary' : 'text-muted-foreground'}
                />
                <span>{option.label}</span>
                {currentSort === option.id && (
                  <Icon name="CheckIcon" size={16} className="ml-auto text-primary" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SortDropdown;